import React, { useState } from 'react';
import { toast } from 'react-hot-toast';

/**
 * LocationPermissionHelp - Help component for enabling location permissions
 * Shows instructions based on device type (iOS/Android)
 */
function LocationPermissionHelp({ isOpen, onClose }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!isOpen) return null;

  // Detect device type
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Enable Location Services
                </h3>
                <div className="mt-4 text-sm text-gray-500 space-y-4">
                  {isIOS ? (
                    <>
                      <div>
                        <p className="font-medium text-gray-700 mb-2">For iPhone/iPad:</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                          <li>Open <strong>Settings</strong> app</li>
                          <li>Scroll down and tap <strong>Safari</strong></li>
                          <li>Tap <strong>Location Services</strong></li>
                          <li>Make sure Location Services is <strong>ON</strong></li>
                          <li>Find this app in the list and select <strong>"While Using the App"</strong></li>
                        </ol>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <p className="text-xs text-blue-800">
                          <strong>Alternative:</strong> If you see the app name in Settings, tap it directly → Location → While Using the App
                        </p>
                      </div>
                    </>
                  ) : isAndroid ? (
                    <>
                      <div>
                        <p className="font-medium text-gray-700 mb-2">For Android:</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                          <li>Open <strong>Settings</strong> app</li>
                          <li>Tap <strong>Apps</strong> or <strong>Application Manager</strong></li>
                          <li>Find and tap this app name</li>
                          <li>Tap <strong>Permissions</strong></li>
                          <li>Find <strong>Location</strong> and tap it</li>
                          <li>Select <strong>"Allow only while using the app"</strong></li>
                        </ol>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <p className="text-xs text-blue-800">
                          <strong>Also check:</strong> Settings → Location → Make sure Location is ON and Mode is set to "High accuracy"
                        </p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <p className="font-medium text-gray-700 mb-2">General Instructions:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Open your device <strong>Settings</strong></li>
                        <li>Find <strong>Privacy</strong> or <strong>Permissions</strong></li>
                        <li>Look for <strong>Location Services</strong></li>
                        <li>Make sure it's enabled</li>
                        <li>Find this app and allow location access</li>
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Got it
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationPermissionHelp;
